import { BoxGeometry, BufferAttribute, BufferGeometry, Group, Mesh } from "three";
/**
 * Slightly bigger than real members (≈200mm posts for an 8'6" tier), so they don't vanish
 * when the whole ship is in view.
 */
export const DEFAULT_LASHING_BRIDGE_MEMBERS_RATIOS = {
    post: 0.08,
    walkway: 0.03,
    brace: 0.05,
    rail: 0.03,
};
export const LASHING_BRIDGES_GROUP_NAME = "LashingBridges";
/**
 * Creates a group with one mesh per lashing bridge, built as an open steel frame:
 * - posts on both faces at every boundary between rows, up to the top walkway;
 * - a walkway at each of the row's `walkways` (computed by bay-grid-pure's
 *   `getLashingBridgeFrameLevels`, shared with the side view);
 * - over the top walkway, the stanchions and the handrails (top and middle, ending at the row's
 *   `yTop`) are thick bars from each face to the clear walkway (`topWalkwayDepth` between them);
 * - diagonal braces under every walkway, on both faces, alternating direction.
 *
 * Stepped bridges and rows without bridge show as they are. Each bridge is one merged
 * geometry (one draw call). The caller adds the group to its scene, in the same frame
 * used to compute the coordinates (e.g. the containers group).
 */
export function createLashingBridgesGroup(bridges, material, ratios = DEFAULT_LASHING_BRIDGE_MEMBERS_RATIOS) {
    const group = new Group();
    group.name = LASHING_BRIDGES_GROUP_NAME;
    bridges.forEach((bridge) => {
        const geoms = buildBridgeFrame(bridge, ratios);
        if (!geoms.length)
            return;
        const merged = mergeBoxes(geoms);
        geoms.forEach((g) => g.dispose());
        const mesh = new Mesh(merged, material);
        mesh.name = `LashingBridge-${bridge.id}`;
        group.add(mesh);
    });
    return group;
}
/** Disposes the geometries of a group created by {@link createLashingBridgesGroup} (not the material) */
export function disposeLashingBridgesGroup(group) {
    group.children.forEach((c) => { var _a; return (_a = c.geometry) === null || _a === void 0 ? void 0 : _a.dispose(); });
    group.clear();
}
function buildBridgeFrame(bridge, ratios) {
    var _a;
    const { x, thickness: t, tierHeight } = bridge;
    if (!(tierHeight > 0))
        return [];
    const rows = bridge.rows.filter((r) => r.yTop > r.yBottom && r.width > 0);
    if (!rows.length)
        return [];
    const post = tierHeight * ratios.post;
    const walkway = tierHeight * ratios.walkway;
    const brace = tierHeight * ratios.brace;
    const rail = tierHeight * ratios.rail;
    const topDepth = Math.min((_a = bridge.topWalkwayDepth) !== null && _a !== void 0 ? _a : t, t);
    /** Fore-aft size of the handrails and stanchions over the top walkway, flush with the faces */
    const fence = topDepth < t ? (t - topDepth) * 0.5 : 0;
    /** x of the members on the aft and fore faces, flush with the given depth */
    const faces = (size, depth = t) => [
        x - depth * 0.5 + size * 0.5,
        x + depth * 0.5 - size * 0.5,
    ];
    const geoms = [];
    const box = (sx, sy, sz, cx, cy, cz) => {
        const g = new BoxGeometry(sx, sy, sz);
        g.translate(cx, cy, cz);
        geoms.push(g);
        return g;
    };
    const framedRows = rows.map((r) => {
        const levels = r.walkways.filter((y) => y > r.yBottom && y <= r.yTop);
        if (!levels.length)
            levels.push(r.yTop);
        return Object.assign(Object.assign({}, r), { levels, topWalkway: levels[levels.length - 1] });
    });
    // 1. Posts at the boundaries between rows: on the faces up to the top walkway, and thick
    //    stanchions (from each face to the clear walkway) up to the top of the structure
    getPosts(framedRows, tierHeight * 0.05).forEach((p) => {
        const faceTop = fence ? p.maxTopWalkway : p.yTop;
        const height = faceTop - p.yBottom;
        faces(post).forEach((fx) => box(post, height, post, fx, p.yBottom + height * 0.5, p.z));
        if (!fence)
            return;
        const sHeight = p.yTop - p.minTopWalkway;
        faces(fence).forEach((fx) => box(fence, sHeight, post, fx, p.minTopWalkway + sHeight * 0.5, p.z));
    });
    framedRows.forEach((r, rowIdx) => {
        const { levels, topWalkway } = r;
        // 2. Walkways, as deep as the structure. Their top surface is at the level, so the slab
        //    stays inside the tier below it
        levels.forEach((y) => box(t, walkway, r.width, x, y - walkway * 0.5, r.z));
        // 3. Handrails over the top walkway, the top one flush with the top of the structure.
        //    As thick as the stanchions (from each face to the clear walkway)
        const railDepth = fence || rail;
        [r.yTop - rail * 0.5, (topWalkway + r.yTop) * 0.5].forEach((y) => faces(railDepth).forEach((fx) => box(railDepth, rail, r.width, fx, y, r.z)));
        // 4. Diagonal braces, one per panel and face, alternating direction
        let yFrom = r.yBottom;
        levels.forEach((yTo, levelIdx) => {
            const h = yTo - yFrom;
            const tilt = Math.atan2(r.width, h);
            const angle = ((rowIdx + levelIdx) % 2 ? -1 : 1) * tilt;
            // Longest bar whose rotated box fits in the panel, so it never pokes through the floor
            const [sin, cos] = [Math.sin(tilt), Math.cos(tilt)];
            const length = Math.min(cos > 1e-6 ? (h - brace * sin) / cos : Infinity, sin > 1e-6 ? (r.width - brace * cos) / sin : Infinity);
            if (!(length > 0) || !Number.isFinite(length)) {
                yFrom = yTo;
                return;
            }
            faces(brace).forEach((fx) => {
                const g = new BoxGeometry(brace, length, brace);
                g.rotateX(angle);
                g.translate(fx, yFrom + h * 0.5, r.z);
                geoms.push(g);
            });
            yFrom = yTo;
        });
    });
    return geoms;
}
/** Boundaries between rows (and the outer edges), with the extent of the rows that touch them */
function getPosts(rows, epsilon) {
    const posts = [];
    rows.forEach((r) => {
        [r.z - r.width * 0.5, r.z + r.width * 0.5].forEach((z) => {
            const existing = posts.find((p) => Math.abs(p.z - z) < epsilon);
            if (existing) {
                existing.yBottom = Math.min(existing.yBottom, r.yBottom);
                existing.yTop = Math.max(existing.yTop, r.yTop);
                existing.minTopWalkway = Math.min(existing.minTopWalkway, r.topWalkway);
                existing.maxTopWalkway = Math.max(existing.maxTopWalkway, r.topWalkway);
            }
            else {
                posts.push({
                    z,
                    yBottom: r.yBottom,
                    yTop: r.yTop,
                    minTopWalkway: r.topWalkway,
                    maxTopWalkway: r.topWalkway,
                });
            }
        });
    });
    return posts;
}
/** Merges indexed box geometries into one indexed geometry (positions and normals) */
function mergeBoxes(geoms) {
    const vertexCount = geoms.reduce((acc, g) => acc + g.getAttribute("position").count, 0);
    const indexCount = geoms.reduce((acc, g) => { var _a, _b; return acc + ((_b = (_a = g.getIndex()) === null || _a === void 0 ? void 0 : _a.count) !== null && _b !== void 0 ? _b : 0); }, 0);
    const positions = new Float32Array(vertexCount * 3);
    const normals = new Float32Array(vertexCount * 3);
    const indices = new Uint32Array(indexCount);
    let vertexOffset = 0;
    let indexOffset = 0;
    geoms.forEach((g) => {
        const pos = g.getAttribute("position");
        positions.set(pos.array, vertexOffset * 3);
        normals.set(g.getAttribute("normal").array, vertexOffset * 3);
        const index = g.getIndex();
        if (index) {
            for (let i = 0; i < index.count; i += 1)
                indices[indexOffset + i] = index.getX(i) + vertexOffset;
            indexOffset += index.count;
        }
        vertexOffset += pos.count;
    });
    const merged = new BufferGeometry();
    merged.setAttribute("position", new BufferAttribute(positions, 3));
    merged.setAttribute("normal", new BufferAttribute(normals, 3));
    merged.setIndex(new BufferAttribute(indices, 1));
    merged.computeBoundingBox();
    return merged;
}
